<template>
  <div v-if="isVisible" class="demo-banner" :class="{ 'with-header': withHeader }">
    <div class="demo-banner-content">
      <span class="demo-icon" aria-hidden="true">👀</span>
      <span class="demo-title">Demo-Modus:</span>
      <span class="demo-text">Beispieldaten lokal im Browser</span>
    </div>
    <div class="demo-banner-actions">
      <router-link to="/login" class="demo-cta-btn">
        Kostenlos Anmelden
      </router-link>
      <button 
        type="button" 
        class="demo-dismiss-btn" 
        @click="dismiss" 
        aria-label="Demo-Hinweis schließen"
        title="Schließen"
      >
        ✕
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAppStore } from '../../stores/appStore'
import { useAuthStore } from '../../stores/authStore'

const props = withDefaults(defineProps<{
  withHeader?: boolean
}>(), {
  withHeader: false
})

const emit = defineEmits<{
  (e: 'update:visible', visible: boolean): void
}>()

const store = useAppStore()
const authStore = useAuthStore()
const route = useRoute()

const isDismissed = ref(sessionStorage.getItem('kochbuch_demo_banner_dismissed') === 'true')

const isVisible = computed(() => {
  return (
    store.isDemoMode &&
    !authStore.isRestoringSession &&
    !isDismissed.value &&
    route.path !== '/login' &&
    route.path !== '/index' &&
    !route.meta?.requiresAdmin
  )
})

watch(isVisible, (val) => {
  emit('update:visible', val)
}, { immediate: true })

const dismiss = () => {
  isDismissed.value = true
  sessionStorage.setItem('kochbuch_demo_banner_dismissed', 'true')
}
</script>

<style scoped>
.demo-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background-color: #2e7d32;
  color: #ffffff;
  padding: 6px 16px;
  font-size: 0.85rem;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
  z-index: 24;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  transition: margin-top 0.2s ease;
}

.demo-banner.with-header {
  margin-top: 56px;
}

:global(body.dark-mode) .demo-banner,
:global(.dark-mode) .demo-banner {
  background-color: #1b5e20;
  border-bottom: 1px solid rgba(139, 195, 75, 0.2);
  color: #e8f5e9;
}

.demo-banner-content {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}

.demo-icon {
  font-size: 1rem;
  line-height: 1;
}

.demo-title {
  font-weight: 700;
  letter-spacing: 0.2px;
}

.demo-text {
  margin: 0;
  opacity: 0.95;
}

.demo-banner-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.demo-cta-btn {
  background-color: #ffffff;
  color: #1b5e20;
  padding: 0.22rem 0.65rem;
  border-radius: 9999px;
  font-weight: 700;
  font-size: 0.76rem;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: all 0.15s ease;
}

.demo-cta-btn:hover {
  background-color: #f1f8e9;
  transform: translateY(-1px);
}

.demo-dismiss-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.85rem;
  line-height: 1;
  width: 24px;
  height: 24px;
  transition: all 0.15s ease;
}

.demo-dismiss-btn:hover {
  color: #ffffff;
  background-color: rgba(255, 255, 255, 0.18);
}

@media (max-width: 540px) {
  .demo-banner {
    padding: 5px 12px;
    gap: 0.5rem;
    justify-content: space-between;
  }

  .demo-text {
    display: none;
  }
}
</style>
